import { useState, useEffect } from "react";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKButton from "components/MKButton";

import DefaultNavbar from "examples/Navbars/DefaultNavbar";
import DefaultFooter from "examples/Footers/DefaultFooter";

import Introduction from "pages/School/Home/sections/Introduction";
import WhyChooseUsPreview from "pages/School/Home/sections/WhyChooseUsPreview";
import KeyFeatures from "pages/School/Home/sections/KeyFeatures";
import CallToAction from "pages/School/Home/sections/CallToAction";

import routes from "routes";
import footerRoutes from "footer.routes";

const heroImages = [
  "/hero-slide-1.jpg",
  "/hero-slide-2.jpg",
  "/hero-slide-3.jpg",
  "/hero-slide-4.jpg",
  "/hero-slide-5.jpg",
];

function Home() {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % heroImages.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  return (
    <>
      {/* Custom Ribbon Header with Logo */}
      <MKBox position="relative">
        {/* Navbar */}
        <DefaultNavbar
          routes={routes}
          action={{
            type: "internal",
            route: "/admissions",
            label: "Enroll Now",
            color: "success",
          }}
          sticky
        />

        {/* Hero Section with Image Slider */}
        <MKBox
          minHeight="100vh"
          width="100%"
          sx={{
            position: "relative",
            display: "grid",
            placeItems: "center",
            overflow: "hidden",
          }}
        >
          {/* Background Image Slider */}
          {heroImages.map((image, index) => (
            <MKBox
              key={index}
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundImage: `url(${image})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                opacity: currentImageIndex === index ? 1 : 0,
                transition: "opacity 2s ease-in-out",
                zIndex: 0,
              }}
            />
          ))}

          {/* Green Ribbon Banner */}
          <MKBox
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              zIndex: 1,
            }}
          >
            {/* Main ribbon body */}
            <MKBox
              sx={{
                width: "100%",
                background: "linear-gradient(180deg, rgba(0,103,71,0.95) 0%, rgba(0,77,53,0.95) 100%)",
                pt: { xs: 10, md: 8 },
                pb: { xs: 4, md: 5 },
              }}
            />
            
            {/* Curved bottom with tails */}
            <MKBox sx={{ position: "relative", height: "80px" }}>
              {/* Left tail */}
              <MKBox
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "42%",
                  height: "50px",
                  background: "linear-gradient(180deg, rgba(200,16,46,0.95) 0%, rgba(160,13,37,0.95) 100%)",
                  clipPath: "polygon(0 0, 100% 0, 85% 100%, 0 70%)",
                }}
              />
              
              {/* Right tail */}
              <MKBox
                sx={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  width: "42%",
                  height: "50px",
                  background: "linear-gradient(180deg, rgba(200,16,46,0.95) 0%, rgba(160,13,37,0.95) 100%)",
                  clipPath: "polygon(0 0, 100% 0, 100% 70%, 15% 100%)",
                }}
              />
              
              {/* Center curved section */}
              <MKBox
                sx={{
                  position: "absolute",
                  top: 0,
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: "30%",
                  height: "70px",
                  background: "linear-gradient(180deg, rgba(0,103,71,0.95) 0%, rgba(0,77,53,0.95) 100%)",
                  borderRadius: "0 0 50% 50%",
                }}
              />
              
              {/* Logo Circle */}
              <MKBox
                sx={{
                  position: "absolute",
                  top: "25px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: { xs: "90px", md: "110px" },
                  height: { xs: "90px", md: "110px" },
                  borderRadius: "50%",
                  backgroundColor: "white",
                  border: "4px solid #C8102E",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  overflow: "hidden",
                  zIndex: 10,
                }}
              >
                <MKBox
                  component="img"
                  src="/school-logo.png"
                  alt="Rose of Sharon Logo"
                  sx={{
                    width: "85%",
                    height: "85%",
                    objectFit: "contain",
                  }}
                  onError={(e) => {
                    e.target.style.display = "none";
                    e.target.nextSibling.style.display = "flex";
                  }}
                />
                <MKBox
                  sx={{
                    display: "none",
                    width: "100%",
                    height: "100%",
                    justifyContent: "center",
                    alignItems: "center",
                    fontSize: "2.5rem",
                    color: "#006747",
                  }}
                >
                  🌹
                </MKBox>
              </MKBox>
            </MKBox>
          </MKBox>

          {/* Hero Content */}
          <Container sx={{ position: "relative", zIndex: 2, mt: { xs: 15, md: 18 } }}>
            <Grid
              container
              item
              xs={12}
              lg={8}
              justifyContent="center"
              alignItems="center"
              flexDirection="column"
              sx={{ mx: "auto", textAlign: "center" }}
            >
              <MKTypography
                variant="h1"
                color="white"
                sx={({ breakpoints, typography: { size } }) => ({
                  textShadow: "2px 2px 4px rgba(0,0,0,0.5)",
                  [breakpoints.down("md")]: {
                    fontSize: size["3xl"],
                  },
                })}
              >
                Rose of Sharon Primary School
              </MKTypography>
              <MKTypography
                variant="h4"
                color="white"
                opacity={0.95}
                mt={1}
                mb={2}
                fontWeight="light"
                fontStyle="italic"
                sx={{ textShadow: "1px 1px 3px rgba(0,0,0,0.5)" }}
              >
                "Nurturing Minds, Building Character, Inspiring Faith"
              </MKTypography>
              <MKTypography 
                variant="body1" 
                color="white" 
                opacity={0.9} 
                mt={1} 
                mb={4} 
                px={{ xs: 2, lg: 8 }}
                sx={{ textShadow: "1px 1px 2px rgba(0,0,0,0.5)" }}
              >
                Welcome to Rose of Sharon Primary School, where we combine academic excellence with 
                Christian values to shape the leaders of tomorrow. Join our community of dedicated 
                educators and enthusiastic learners.
              </MKTypography>
              <Grid container spacing={2} justifyContent="center">
                <Grid item>
                  <MKButton
                    variant="gradient"
                    color="success"
                    size="large"
                    component="a"
                    href="/admissions"
                  >
                    Enroll Now
                  </MKButton>
                </Grid>
                <Grid item>
                  <MKButton
                    variant="outlined"
                    color="white"
                    size="large"
                    component="a"
                    href="/contact"
                  >
                    Contact Us
                  </MKButton>
                </Grid>
              </Grid>
            </Grid>
          </Container>
        </MKBox>
      </MKBox>
      <Card
        sx={{
          p: 2,
          mx: { xs: 2, lg: 3 },
          mt: -8,
          mb: 4,
          backgroundColor: ({ palette: { white }, functions: { rgba } }) => rgba(white.main, 0.8),
          backdropFilter: "saturate(200%) blur(30px)",
          boxShadow: ({ boxShadows: { xxl } }) => xxl,
        }}
      >
        <Introduction />
        <WhyChooseUsPreview />
        <KeyFeatures />
        <CallToAction />
      </Card>
      <MKBox pt={6} px={1} mt={6}>
        <DefaultFooter content={footerRoutes} />
      </MKBox>
    </>
  );
}

export default Home;
